import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AddSchemeDetails from './AddSchemeDetails';
import AddProductDetails from './AddProductDetails';
import { arrayToDropdownOptions } from '../../../../utils/sms/arrayToDropdownOptions';
import { Button, Card } from '@themesberg/react-bootstrap';
import {
  addNewScheme,
  getAllCompaniesCategoriesSubCategoriesData,
  getProductsByCompaniesCategoriesSubCategories,
} from '../../../../parse-functions/sms/scheme';
import { updateSpinnerState } from '../../../../actions/spinner';
import { updateToastInfo } from '../../../../actions/settings';
import {
  addSchemeValidate,
  validateProductFetch,
} from '../../validate/schemeValidate';
import BreadCrumbTitle from '../common/BreadCrumbTitle';
import { schemeTypeMap } from '../../../../utils/sms/constantsAndUtils';
import { ViewProductsTable } from '../../../../utils/sms/schemeTableRedesign';
import BulkAddSchemeModal from '../BulkAddSchemeModal';
import { parseUploadedExcelData } from '../../../../utils/sms/excelUpload';
import { setSchemesDataInStore } from '../../../../actions/sms/schemes';

const AddSchemeRedesign = ({ editData, handleSave }) => {
  const initialFormInput = editData
    ? editData
    : {
        company: [],
        schemeCircularNo: '',
        schemeCircularDate: '',
        validFrom: '',
        validTill: '',
        regions: [],
        type: [],
        remarks: '',
        uploadExcel: false,
        sellIntype: [],
        category: [],
        subCategory: [],
        subCategory2: [],
        subCategory3: [],
        payoutBasis: [],
        products: [{ productName: [], valuationType: [], payout: '' }],
        schemeCircularFile: null,
      };
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cloneSchemeData = useSelector(
    (state) => state.schemes?.cloneSchemeData || {}
  );
  const [formInput, setFormInput] = useState(initialFormInput);
  const [formErrors, setFormErrors] = useState({});
  const [categoryOptions, setCategoryOptions] = useState({
    categoryOptions: [],
    subCategory1Options: [],
    subCategory2Options: [],
    subCategory3Options: [],
  });
  const [subCategoryOptions, setSubCategoryOptions] = useState();
  const [productOptions, setProductOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [formProps, setFormProps] = useState({
    showCappingType: editData?.type?.[0] === 'Sell-out' ? true : false,
    copyCappingType: false,
    copyPayoutValue: true,
    copyValuationType: true,
    addTarget: false,
  });
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const userInfo = useSelector((state) => state?.user?.userInfo);
  const handleAddScheme = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const valid = addSchemeValidate(formInput, setFormErrors, formProps);
      if (!valid) {
        dispatch(updateSpinnerState(false));
        return;
      }
      let data = formInput;
      data.products = data.products?.map((item) => {
        return {
          ...item,
          productName: Array.isArray(item.productName)
            ? item.productName[0]
            : item.productName,
          vanNo:
            item.vanNo ||
            (Array.isArray(item.productName)
              ? products[item.productName[0]]
              : products[item.productName]),
          cappingType:
            formProps.showCappingType && Number(item.cappingQuantity) > 0
              ? item.cappingType
              : ['No Capping'],
          cappingQuantity: formProps.showCappingType
            ? item.cappingQuantity
            : '',
          valuationType: Array.isArray(item.valuationType)
            ? item.valuationType[0]
            : item.valuationType,
        };
      });
      if (editData) {
        await handleSave(data);
      } else {
        await addNewScheme(data);
      }
      dispatch(updateSpinnerState(false));
      dispatch(setSchemesDataInStore({ type: 'cloneSchemeData', data: {} }));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: '',
          message: editData
            ? t(`Scheme edited Successfully.`)
            : t(`Scheme created Successfully.`),
        })
      );
      setFormInput(initialFormInput);
      setProductOptions([]);
      setProducts([]);
      setFormProps({
        showCappingType: false,
        copyCappingType: false,
        copyPayoutValue: true,
        copyValuationType: true,
      });
    } catch (error) {
      console.log(error);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: editData
            ? t(`Scheme edit failed.`)
            : t(`Scheme creation failed.`),
          message: error.message,
        })
      );
    }
  };
  const fileUploadComplete = (file) => {
    const { validProducts, showCappingType, addTarget, errors } =
      parseUploadedExcelData(file, formInput, dispatch);

    if (errors.size > 0) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: '',
          message: `Upload valid excel file with correct formatting for ${Array.from(
            errors
          ).join(', ')}`,
        })
      );
      return;
    }
    setFormInput((prev) => ({
      ...prev,
      products: validProducts,
    }));

    if (showCappingType) {
      setFormProps((prev) => ({
        ...prev,
        showCappingType: true,
      }));
    }

    if (addTarget) {
      setFormProps((prev) => ({
        ...prev,
        addTarget: true,
      }));
    }
  };

  const fetchProducts = async () => {
    const valid = validateProductFetch(formInput, setFormErrors);
    if (!valid) {
      return;
    } else {
      try {
        dispatch(updateSpinnerState(true));
        const options = await getProductsByCompaniesCategoriesSubCategories({
          companiesName: formInput.company,
          categoriesName: formInput.category,
          subCategories: formInput.subCategory,
          subCategory2: formInput.subCategory2,
          subCategory3: formInput.subCategory3,
        });
        const productNames = options?.map((item) => item.productName);
        setProducts(
          options.reduce((acc, item) => {
            if (!acc[item.productName]) {
              acc[item.productName] = item.vanNo;
            }
            return acc;
          }, {})
        );
        setProductOptions(arrayToDropdownOptions(productNames));
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'success',
            title: '',
            message: t(`Successfully fetched Products.`),
          })
        );
      } catch (error) {
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: '',
            message: t(`Error while fetching products.`),
          })
        );
      }
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(updateSpinnerState(true));
        let data = [];
        if (formInput.company.length > 0 && isAdmin) {
          data = await getAllCompaniesCategoriesSubCategoriesData({
            isCategory: true,
            company: formInput.company[0],
          });
          setCategoryOptions((prev) => ({
            ...prev,
            categoryOptions: arrayToDropdownOptions(
              data?.map((item) => item.categoryName)
            ),
          }));
        } else {
          data = arrayToDropdownOptions(
            userInfo?.productCategory?.filter((item) => item !== 'All') || []
          );
          setCategoryOptions((prev) => ({
            ...prev,
            categoryOptions: data,
          }));
        }

        dispatch(updateSpinnerState(false));
      } catch (error) {
        dispatch(updateSpinnerState(false));
        console.log(error);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: '',
            message: t(`Error while fetching categories.`),
          })
        );
      }
    };
    fetchData();
  }, [formInput.company]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(updateSpinnerState(true));
        let data = [];
        if (formInput.category?.length > 0) {
          data = await getAllCompaniesCategoriesSubCategoriesData({
            isAllSubCategory: true,
            company: formInput.company[0],
            category: formInput.category,
          });
          setSubCategoryOptions(data);
          setCategoryOptions((prev) => ({
            ...prev,
            subCategory1Options: arrayToDropdownOptions(
              Array.from(
                new Set(
                  data
                    ?.filter((item) => item.productSubCategory1)
                    .map((item) => item.productSubCategory1)
                )
              )
            ),
          }));
        }
        dispatch(updateSpinnerState(false));
      } catch (error) {
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: '',
            message: t(`Error while fetching subCategories.`),
          })
        );
      }
    };
    fetchData();
  }, [formInput.category]);

  useEffect(() => {
    if (Object.keys(cloneSchemeData).length > 0)
      setFormInput({
        ...cloneSchemeData,
        category: cloneSchemeData.productCategory,
        products: cloneSchemeData.products.map((item) => ({
          ...item,
          valuationType: [item.valuationType],
        })),
      });
    setFormProps({
      ...formProps,
      showCappingType: cloneSchemeData.products?.some(
        (item) => item.cappingType
      ),
      addTarget: cloneSchemeData.products?.some((item) => item.target),
    });
  }, [cloneSchemeData]);
  return (
    <div className="p-2">
      {!editData && (
        <BreadCrumbTitle breadcrumbs={[{ title: t('Add New Scheme') }]} />
      )}
      <Card className="mt-4 p-2 mb-2">
        <h5 className="m-0">Scheme Details</h5>
      </Card>
      <AddSchemeDetails
        formInput={formInput}
        setFormInput={setFormInput}
        formErrors={formErrors}
        handleFileUpload={fileUploadComplete}
        setFormProps={setFormProps}
      />
      <Card className="mt-4 p-2 mb-2">
        <h5 className="m-0">Product Details</h5>
      </Card>
      <AddProductDetails
        formInput={formInput}
        setFormInput={setFormInput}
        formProps={formProps}
        setFormProps={setFormProps}
        formErrors={formErrors}
        productOptions={productOptions}
        categoryOptions={categoryOptions}
        subCategoryOptions={subCategoryOptions}
        setCategoryOptions={setCategoryOptions}
        fetchProducts={fetchProducts}
      />
      <div className="d-flex justify-content-end mt-4 gap-2">
        <Button
          style={{
            backgroundColor: '#F0F0F0',
            color: 'black',
          }}
          onClick={() => setShowProductsModal(true)}
        >
          View Products
        </Button>
        <Button
          style={{
            backgroundColor: '#F0F0F0',
            color: 'black',
          }}
          onClick={handleAddScheme}
        >
          {editData ? 'Save Update' : 'Create Scheme'}
        </Button>
      </div>
      <BulkAddSchemeModal
        data={formInput.products || []}
        showModal={showProductsModal}
        closeModal={() => setShowProductsModal(false)}
        columns={ViewProductsTable(formInput)}
        schemeType={formInput.type?.[0]}
      />
    </div>
  );
};
export default AddSchemeRedesign;
